/* eslint-disable valid-jsdoc */
// frontend/packages/common/utils/widgetLogger.js

/**
 * Système de logging unifié pour les widgets
 * - Logs conditionnés selon l'environnement
 * - Format cohérent
 * - Niveaux de logging appropriés
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Configuration du logging par type
const LOG_CONFIG = {
  // En production, seules les erreurs critiques sont loggées
  production: {
    api: false,
    error: false, // Seules les erreurs vraiment critiques
    warn: false,
    info: false,
    debug: false,
  },
  // En développement, logging sélectif
  development: {
    api: true, // Appels API importants seulement
    error: true, // Toutes les erreurs
    warn: true, // Avertissements importants
    info: false, // Infos désactivées par défaut
    debug: false, // Debug désactivé par défaut
  },
};

const currentConfig = isDevelopment ? LOG_CONFIG.development : LOG_CONFIG.production;

// Système de throttling pour éviter les logs dupliqués
const logThrottleMap = new Map();
const THROTTLE_DURATION = 5000; // 5 secondes

/**
 * Vérifie si un message peut être loggé (throttling)
 */
const canLog = (key, message) => {
  const now = Date.now();
  const lastLogged = logThrottleMap.get(key);

  if (!lastLogged || (now - lastLogged.timestamp) > THROTTLE_DURATION) {
    logThrottleMap.set(key, {timestamp: now, message});
    return true;
  }

  // Si c'est le même message, on ne log pas
  if (lastLogged.message === message) {
    return false;
  }

  return true;
};

// Contexte des erreurs en cours pour éviter la redondance
let currentErrorContext = null;

/**
 * Logger principal pour les widgets
 */
export const widgetLogger = {
  /**
   * Log des erreurs importantes (toujours en dev, jamais en prod pour les widgets)
   */
  error: (message, data = null) => {
    if (currentConfig.error && canLog('error', message)) {
      if (data) {
        console.error(`🔴 [Widget Error] ${message}`, data);
      } else {
        console.error(`🔴 [Widget Error] ${message}`);
      }
    }
  },

  /**
   * Log des avertissements (API key manquante, etc.)
   */
  warn: (message, data = null) => {
    if (currentConfig.warn && canLog('warn', message)) {
      if (data) {
        console.warn(`🟡 [Widget] ${message}`, data);
      } else {
        console.warn(`🟡 [Widget] ${message}`);
      }
    }
  },

  /**
   * Log des appels API importants (succès/échec)
   */
  api: {
    request: (method, url, hasApiKey = false) => {
      if (currentConfig.api) {
        const cleanUrl = url.replace(/\/[a-fA-F0-9]{24}/, '/[ID]'); // Nettoyer les IDs MongoDB
        const key = `api-request-${method}-${cleanUrl}`;

        if (canLog(key, `${method} ${cleanUrl}`)) {
          console.log(`🔵 [API] ${method.toUpperCase()} ${cleanUrl} ${hasApiKey ? '🔑' : '❌'}`);
        }
      }
    },

    success: (method, url, status) => {
      if (currentConfig.api) {
        const cleanUrl = url.replace(/\/[a-fA-F0-9]{24}/, '/[ID]');
        console.log(`✅ [API] ${method.toUpperCase()} ${cleanUrl} (${status})`);
        // Reset error context on success
        currentErrorContext = null;
      }
    },

    error: (method, url, status, error = null) => {
      if (currentConfig.api) {
        const cleanUrl = url.replace(/\/[a-fA-F0-9]{24}/, '/[ID]');
        const errorMsg = error?.message || 'Unknown error';
        const key = `api-error-${method}-${cleanUrl}-${status}`;

        // Éviter les logs d'erreur redondants si on vient de logger un problème d'auth
        if (status === 401 && currentErrorContext === 'auth') {
          return; // Skip ce log car on a déjà loggé le problème d'auth
        }

        if (canLog(key, `${method} ${cleanUrl} ${status}`)) {
          console.log(`❌ [API] ${method.toUpperCase()} ${cleanUrl} (${status}) - ${errorMsg}`);
        }
      }
    },
  },

  /**
   * Log d'informations générales (désactivé par défaut)
   */
  info: (message, data = null) => {
    if (currentConfig.info && canLog('info', message)) {
      if (data) {
        console.info(`ℹ️ [Widget] ${message}`, data);
      } else {
        console.info(`ℹ️ [Widget] ${message}`);
      }
    }
  },

  /**
   * Log de debug détaillé (désactivé par défaut)
   */
  debug: (message, data = null) => {
    if (currentConfig.debug && canLog('debug', message)) {
      if (data) {
        console.debug(`🔍 [Debug] ${message}`, data);
      } else {
        console.debug(`🔍 [Debug] ${message}`);
      }
    }
  },
};

/**
 * Logger spécialisé pour les erreurs d'authentification
 */
export const authLogger = {
  missingApiKey: () => {
    currentErrorContext = 'auth';
    widgetLogger.warn('🔐 Configuration : API key manquante');
  },

  invalidApiKey: (apiKey) => {
    currentErrorContext = 'auth';
    widgetLogger.warn(`🔐 Configuration : Format de clé API invalide (${apiKey?.substring(0, 8)}...)`);
  },

  authenticationError: () => {
    // Ne log que si on n'a pas déjà loggé un problème d'auth
    if (currentErrorContext !== 'auth') {
      widgetLogger.warn('🔐 Authentification échouée - Vérifiez votre API key');
    }
  },

  accessDenied: () => {
    widgetLogger.warn('🔐 Accès interdit - Permissions insuffisantes');
  },
};

/**
 * Configuration dynamique du logging (pour debug temporaire)
 */
export const configureLogging = (config) => {
  if (isDevelopment) {
    Object.assign(currentConfig, config);
    widgetLogger.info('Configuration de logging mise à jour', currentConfig);
  }
};

// Export de la configuration actuelle pour inspection
export const getLogConfig = () => ({...currentConfig, isDevelopment});

// Nettoyage périodique du throttling pour éviter les fuites mémoire
if (isDevelopment) {
  setInterval(() => {
    const now = Date.now();
    for (const [key, value] of logThrottleMap.entries()) {
      if (now - value.timestamp > THROTTLE_DURATION * 2) {
        logThrottleMap.delete(key);
      }
    }
  }, THROTTLE_DURATION);
}

/**
 * Fonction globale pour activer temporairement tous les logs en développement
 * Utilisation en console: window.enableWidgetDebug()
 */
if (isDevelopment && typeof window !== 'undefined') {
  window.enableWidgetDebug = () => {
    configureLogging({
      api: true,
      error: true,
      warn: true,
      info: true,
      debug: true,
    });
    console.log('🔧 Widget debug logs enabled - All logs active');
  };

  window.disableWidgetDebug = () => {
    configureLogging({
      api: true,
      error: true,
      warn: true,
      info: false,
      debug: false,
    });
    console.log('🔧 Widget debug logs disabled - Back to normal');
  };

  window.getWidgetLogStats = () => {
    console.table({
      'Throttled entries': logThrottleMap.size,
      'Current context': currentErrorContext,
      'Config': currentConfig,
    });
  };
}
